import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaUpload } from 'react-icons/fa';
import { getEmisor, guardarEmisor, getCertificado, subirCertificado, getFolios, subirFolios } from '../../services/facturadorService';
import { NOMBRES_TIPO, FORMAS_PAGO, fechaCorta, fechaChile, mensajeError } from './comun';
import '../../styles/pages/Facturador.css';

const ESTADOS_RANGO = { activo: 'fe-badge--verde', agotado: 'fe-badge--gris', vencido: 'fe-badge--rojo' };

const ConfiguracionFacturadorPage = ({ id_company }) => {
  const navigate = useNavigate();
  const [emisor, setEmisor] = useState(null);
  const [form, setForm] = useState(null);
  const [certificado, setCertificado] = useState(null);
  const [folios, setFolios] = useState([]);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [archivoCertificado, setArchivoCertificado] = useState(null);
  const [clave, setClave] = useState('');
  const [archivoCaf, setArchivoCaf] = useState(null);
  const [subiendo, setSubiendo] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [datosEmisor, datosCertificado, rangos] = await Promise.all([
        getEmisor(id_company),
        getCertificado(id_company),
        getFolios(id_company),
      ]);
      setEmisor(datosEmisor);
      setForm({
        giro: datosEmisor.giro || '',
        actividades: (datosEmisor.actividades || []).join(', '),
        sucursal_codigo: datosEmisor.sucursal_codigo ?? '',
        forma_pago_defecto: datosEmisor.forma_pago_defecto,
        unidad_sii: datosEmisor.unidad_sii || '',
      });
      setCertificado(datosCertificado);
      setFolios(rangos);
    } catch (err) {
      setError(mensajeError(err, 'No se pudo cargar la configuración del facturador'));
    }
  }, [id_company]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const actividades = form.actividades.split(/[\s,;]+/).filter(Boolean).map(Number);
      const actualizado = await guardarEmisor(id_company, {
        giro: form.giro,
        actividades,
        sucursal_codigo: form.sucursal_codigo === '' ? '' : Number(form.sucursal_codigo),
        forma_pago_defecto: Number(form.forma_pago_defecto),
        unidad_sii: form.unidad_sii,
      });
      setEmisor(actualizado);
      Swal.fire({ icon: 'success', title: 'Guardado', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudieron guardar los datos') });
    } finally {
      setGuardando(false);
    }
  };

  const enviarCertificado = async (e) => {
    e.preventDefault();
    if (!archivoCertificado || !clave) {
      Swal.fire({ icon: 'warning', title: 'Atención', text: 'Selecciona el archivo .pfx o .p12 e ingresa su clave' });
      return;
    }
    setSubiendo('certificado');
    try {
      const nuevo = await subirCertificado(id_company, archivoCertificado, clave);
      setCertificado(nuevo);
      setArchivoCertificado(null);
      setClave('');
      e.target.reset();
      Swal.fire({ icon: 'success', title: 'Certificado cargado', text: `Firma ${nuevo.nombre_titular}` });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo cargar el certificado') });
    } finally {
      setSubiendo(null);
    }
  };

  const enviarCaf = async (e) => {
    e.preventDefault();
    if (!archivoCaf) {
      Swal.fire({ icon: 'warning', title: 'Atención', text: 'Selecciona el archivo CAF (.xml) que descargaste del SII' });
      return;
    }
    setSubiendo('caf');
    try {
      const rango = await subirFolios(id_company, archivoCaf);
      setArchivoCaf(null);
      e.target.reset();
      setFolios(await getFolios(id_company));
      Swal.fire({ icon: 'success', title: 'Folios cargados', text: `${NOMBRES_TIPO[rango.tipo] || rango.tipo}: folios ${rango.folio_desde} a ${rango.folio_hasta}` });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo cargar el CAF') });
    } finally {
      setSubiendo(null);
    }
  };

  return (
    <div className="fe-page">
      <header className="fe-header">
        <button className="fe-back-btn" onClick={() => navigate('/facturador')}>
          <FaArrowLeft /> Volver
        </button>
        <div className="fe-header-titulo">
          <div>
            <h1 className="fe-title">Configuración del facturador</h1>
            <p className="fe-sub">Datos del emisor, certificado digital y folios</p>
          </div>
        </div>
      </header>

      {error && <div className="fe-aviso fe-aviso--rojo">{error}</div>}
      {!error && !emisor && <p className="fe-loading">Cargando...</p>}

      {emisor && form && (
        <>
          <div className="fe-panel">
            <h2 className="fe-panel-title">Lo que define EFE</h2>
            <dl className="fe-datos">
              <dt>Ambiente del SII</dt>
              <dd>{emisor.ambiente === 'produccion' ? 'Producción' : 'Certificación (pruebas)'}</dd>
              <dt>Documentos habilitados</dt>
              <dd>{emisor.tipos_habilitados.map((t) => NOMBRES_TIPO[t] || t).join(', ')}</dd>
              <dt>Resolución del SII</dt>
              <dd>{emisor.resolucion_fecha ? `N° ${emisor.resolucion_numero} del ${fechaCorta(emisor.resolucion_fecha)}` : 'Pendiente'}</dd>
            </dl>
            <p className="fe-hint">
              El RUT, la razón social, la dirección y la comuna del emisor salen de los <Link to="/configuracion">datos de la empresa</Link>.
            </p>
          </div>

          <form className="fe-panel" onSubmit={guardar}>
            <h2 className="fe-panel-title">Datos del emisor</h2>
            <div className="fe-form-grid">
              <div className="fe-form-group fe-form-group--wide">
                <label>Giro</label>
                <input name="giro" value={form.giro} maxLength={80} onChange={cambiar} placeholder="Como figura en el SII" />
              </div>
              <div className="fe-form-group fe-form-group--wide">
                <label>Actividades económicas</label>
                <input name="actividades" value={form.actividades} onChange={cambiar} placeholder="Códigos del SII, hasta 4: 692000, 620200" />
              </div>
              <div className="fe-form-group">
                <label>Forma de pago por defecto</label>
                <select name="forma_pago_defecto" value={form.forma_pago_defecto} onChange={cambiar}>
                  {Object.entries(FORMAS_PAGO).map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
                </select>
              </div>
              <div className="fe-form-group">
                <label>Oficina del SII</label>
                <input name="unidad_sii" value={form.unidad_sii} maxLength={60} onChange={cambiar} placeholder="Ej.: Santiago Oriente" />
              </div>
              <div className="fe-form-group">
                <label>Código de sucursal SII <span className="fe-muted">(opcional)</span></label>
                <input name="sucursal_codigo" type="number" min="1" value={form.sucursal_codigo} onChange={cambiar} />
              </div>
            </div>
            <p className="fe-hint">La oficina del SII es la que corresponde al domicilio de la empresa; va impresa bajo el recuadro del folio.</p>
            <div className="fe-form-actions">
              <button type="submit" className="fe-btn fe-btn--success" disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>

          <div className="fe-panel">
            <h2 className="fe-panel-title">Certificado digital</h2>
            {certificado ? (
              <dl className="fe-datos">
                <dt>Firma</dt><dd>{certificado.nombre_titular} · {certificado.rut_titular}</dd>
                <dt>Vence</dt><dd>{fechaChile(certificado.fecha_vencimiento)}</dd>
              </dl>
            ) : (
              <div className="fe-aviso fe-aviso--amarillo">No hay certificado cargado: sin él no se puede firmar.</div>
            )}
            <form onSubmit={enviarCertificado}>
              <div className="fe-form-grid">
                <div className="fe-form-group">
                  <label>Archivo (.pfx o .p12)</label>
                  <input type="file" accept=".pfx,.p12" onChange={(e) => setArchivoCertificado(e.target.files[0] || null)} />
                </div>
                <div className="fe-form-group">
                  <label>Clave del certificado</label>
                  <input type="password" value={clave} onChange={(e) => setClave(e.target.value)} autoComplete="new-password" />
                </div>
              </div>
              <p className="fe-hint">El certificado de la persona que firma por la empresa. Se guarda cifrado y reemplaza al anterior.</p>
              <div className="fe-form-actions">
                <button type="submit" className="fe-btn fe-btn--primary" disabled={subiendo === 'certificado'}>
                  <FaUpload /> {subiendo === 'certificado' ? 'Cargando...' : certificado ? 'Reemplazar certificado' : 'Cargar certificado'}
                </button>
              </div>
            </form>
          </div>

          <div className="fe-panel">
            <h2 className="fe-panel-title">Folios (CAF)</h2>
            {folios.length === 0 ? (
              <div className="fe-aviso fe-aviso--amarillo">No hay folios cargados. Pídelos en sii.cl (Timbraje electrónico) y sube el archivo aquí.</div>
            ) : (
              <div className="fe-table-container" style={{ marginBottom: 14 }}>
                <table className="fe-table">
                  <thead>
                    <tr>
                      <th>Documento</th><th>Ambiente</th><th className="fe-num">Rango</th><th className="fe-num">Quedan</th><th>Vencen</th><th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {folios.map((r) => (
                      <tr key={r.id}>
                        <td>{NOMBRES_TIPO[r.tipo] || r.tipo}</td>
                        <td>{r.ambiente === 'produccion' ? 'Producción' : 'Certificación'}</td>
                        <td className="fe-num">{r.folio_desde} – {r.folio_hasta}</td>
                        <td className="fe-num">{r.estado === 'activo' ? r.disponibles : 0}</td>
                        <td>{fechaCorta(r.vence_el)}</td>
                        <td><span className={`fe-badge ${ESTADOS_RANGO[r.estado] || 'fe-badge--gris'}`}>{r.estado}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <form onSubmit={enviarCaf}>
              <div className="fe-form-grid">
                <div className="fe-form-group">
                  <label>Archivo CAF (.xml)</label>
                  <input type="file" accept=".xml" onChange={(e) => setArchivoCaf(e.target.files[0] || null)} />
                </div>
              </div>
              <p className="fe-hint">Los folios vencen 6 meses después de autorizados: pide rangos que alcances a usar.</p>
              <div className="fe-form-actions">
                <button type="submit" className="fe-btn fe-btn--primary" disabled={subiendo === 'caf'}>
                  <FaUpload /> {subiendo === 'caf' ? 'Cargando...' : 'Cargar folios'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default ConfiguracionFacturadorPage;
